// The Deck Builder: browse every card, filter, build a 60 + 1 Emperor deck,
// view / export / import / save it.
import { CONFIG } from '../config.ts';
import { ARCHETYPES, leaningsOf } from '../shared/archetypes.ts';
import { adviseDeck, suggestCards } from '../shared/deckAdvice.ts';
import { showPackPicker, PACKS_PER_DECK } from './packOpening.ts';
import { buildRandomDeck, themeText } from '../shared/randomDeck.ts';
import { RARITIES, CARD_TYPES, AFFILIATIONS, COSTS, ARCHETYPE_IDS, ROLES, type CardDef, costLabel, titleCase, rarityLabel, isEmperor, roman, cardNo } from '../shared/cards.ts';
import {
  type Deck, whyCantAdd, addCard, removeCard, deckStats, deckProblems, deckEntries,
  exportDeck, importDeck, MAIN_DECK_SIZE, DEFAULT_DECK_NAME,
} from '../shared/deck.ts';
import { CARD_DB } from './data.ts';
import { cardFrontSrc, setImg, setImgLazy } from './images.ts';
import { showDeckStats } from './deckStats.ts';
import { loadCardStats, statsFor } from './cardStats.ts';
import type { CardStats } from '../shared/online.ts';
import { cardRulesNode } from './cardRules.ts';
import { session } from './session.ts';
import { rememberSavedDeck } from './deckMemory.ts';
import { isTouchScreen } from './touch.ts';
import { el, button, toast, modal, showCardLarge, copyText } from './ui.ts';

// Filters stay as you left them for the rest of the session.
const filters = {
  set: new Set<string>(),
  rarity: new Set<string>(),
  type: new Set<string>(),
  cost: new Set<number>(),
  affiliation: new Set<string>(),
  leans: new Set<string>(),
  roles: new Set<string>(),
  search: '',
};

// How the cards are put in order (Most used and Best win % only while Card Stats is on), and whether the
// Deck Helper (what the deck needs, cards that fit) is open. Both stay as you left them.
// The four stat orders each come highest first or (ending in Low) lowest first.
type StatSort = 'use' | 'win' | 'inDecks' | 'deckWin';
type SortBy = 'set' | 'name' | 'cost' | 'rarity' | StatSort | `${StatSort}Low`;
const view = { sort: 'set' as SortBy, helper: true };

// The Card Stats toggle: each card shows how often it is played online and how often it wins. Stays as you left it.
const statsView = { on: false };
let liveStats: CardStats | null = null;

/** Each archetype's searchable words: its name and id ("counter / punish counter", "direct attack direct_attack"). */
const ARCHETYPE_WORDS = new Map(ARCHETYPES.map((a) => [a.id, `${a.name} ${a.id}`.toLowerCase()]));
/** The archetype a search names outright, if it does ("ramp", "Direct attack", "alt win", "leans: voltron"). */
function archetypeNamed(q: string): string | null {
  const norm = (t: string) => t.toLowerCase().replace(/^leans?:?\s+/, '').replace(/[\s_\-/]+/g, ' ').trim();
  const want = norm(q);
  if (!want) return null;
  for (const a of ARCHETYPES) {
    const names = [norm(a.name), norm(a.id), ...a.name.split('/').map(norm)];
    if (a.id === 'alt_win') names.push('alt win', 'alternate win');
    if (names.includes(want)) return a.id;
  }
  return null;
}

function matches(card: CardDef) {
  const f = filters;
  if (f.set.size && !f.set.has(card.set)) return false;
  if (f.rarity.size && !f.rarity.has(card.rarity)) return false;
  if (f.type.size && !card.types.some((t) => f.type.has(t))) return false;
  if (f.cost.size && (card.cost === null || !f.cost.has(card.cost))) return false;
  if (f.affiliation.size && !card.affiliations.some((a) => f.affiliation.has(a))) return false;
  if (f.leans.size && ![...leaningsOf(card).keys()].some((id) => f.leans.has(id))) return false;
  if (f.roles.size && !card.roles.some((r) => f.roles.has(r))) return false;
  if (f.search) {
    const q = f.search.toLowerCase();
    if (/^#?\d+$/.test(q)) return String(card.setNumber ?? card.number) === q.replace('#', '');
    // an archetype's name ("ramp", "direct attack", "leans voltron"): the cards that lean that way
    const arch = archetypeNamed(q);
    if (arch) return leaningsOf(card).has(arch);
    // every word has to be somewhere on the card: its name, its text, its types, traits or affiliations
    const hay = [card.name, card.text, card.flavor, card.types.join(' '), card.traits.join(' '), card.affiliations.join(' '), card.emperorAbilities.map((a) => `${a.name} ${a.text}`).join(' '), [...leaningsOf(card).keys()].map((id) => ARCHETYPE_WORDS.get(id) ?? id).join(' ')].join(' ').toLowerCase();
    if (!q.split(/\s+/).every((w) => hay.includes(w))) return false;
  }
  return true;
}

function sameDeck(a: Deck | null, b: Deck) {
  if (!a || a.size !== b.size) return false;
  for (const [k, v] of a) if (b.get(k) !== v) return false;
  return true;
}

/**
 * @param onBack where the Menu button goes
 * @param afterSave if given, saving a deck goes straight there (used when Play sent you
 *   here because you had no deck yet: it starts the game mode you had already chosen)
 */
export function showDeckBuilder(onBack: () => void, afterSave?: () => void) {
  const deck = session.workingDeck;
  if (deck.size === 0 && session.savedDeck) {
    for (const [k, v] of session.savedDeck) deck.set(k, v);
    session.workingDeckName = session.savedDeckName;
  }
  const deckName = () => session.workingDeckName.trim() || DEFAULT_DECK_NAME;
  const nameInput = el('input', {
    id: 'deck-name',
    class: 'text-input deck-name',
    maxlength: '40',
    placeholder: 'Name this deck',
    value: session.workingDeckName,
    autocomplete: 'off',
  });
  nameInput.addEventListener('input', () => {
    session.workingDeckName = nameInput.value;
    refreshCounts();
  });

  const badges = new Map<number, HTMLElement>();
  const emperorCount = el('span', { class: 'count-value' });
  const mainCount = el('span', { class: 'count-value' });
  const saveState = el('span', { class: 'save-state' });
  const grid = el('div', { class: 'card-grid', id: 'card-grid' });
  const resultCount = el('span', { class: 'result-count' });
  const helper = el('section', { class: 'deck-helper', id: 'deck-helper', 'aria-label': 'Deck helper' });
  const packState = el('span', { class: 'pack-state', hidden: true });

  // ---------- a deck made from packs ----------
  // While a deck is being made from packs, it can't be changed by hand: Clear Deck starts over.
  const packLocked = () => session.packRun !== null;
  const lockedToast = () => toast('This deck is made from packs, so it can’t be changed by hand. Clear Deck to start over.', 'info');

  // ---------- counts & badges ----------
  function refreshCounts() {
    const { emperors, main } = deckStats(deck, CARD_DB);
    emperorCount.textContent = `${emperors}/1`;
    emperorCount.classList.toggle('full', emperors === 1);
    mainCount.textContent = `${main}/${MAIN_DECK_SIZE}`;
    mainCount.classList.toggle('full', main === MAIN_DECK_SIZE);
    const saved = sameDeck(session.savedDeck, deck) && session.savedDeckName === deckName();
    saveState.textContent = saved ? 'Saved — this is your play deck' : session.savedDeck ? 'Unsaved changes' : 'Not saved yet';
    saveState.classList.toggle('is-saved', saved);
    for (const [num, badge] of badges) {
      const n = deck.get(num) ?? 0;
      badge.textContent = n ? `×${n}` : '';
      badge.hidden = n === 0;
    }
    const run = session.packRun;
    packState.hidden = !run;
    if (run) packState.textContent = `Pack deck · ${run.opened.length}/${PACKS_PER_DECK} packs`;
    scheduleHelper();
  }

  // ---------- Deck Helper: what the deck needs, and cards that fit ----------
  let helperQueued = false;
  function scheduleHelper() {
    if (helperQueued) return;
    helperQueued = true;
    requestAnimationFrame(() => {
      helperQueued = false;
      renderHelper();
    });
  }
  function renderHelper() {
    helper.hidden = !view.helper;
    if (!view.helper) return;
    const { emperors, main } = deckStats(deck, CARD_DB);
    if (emperors + main === 0) session.workingDeckAim = null; // emptied card by card: a fresh start
    const advice = adviseDeck(deck, CARD_DB, session.workingDeckAim);
    const archName = advice.archetype ? (ARCHETYPES.find((a) => a.id === advice.archetype)?.name ?? titleCase(advice.archetype)) : null;
    const needs = advice.needs.filter((n) => n.id !== 'cards' && (n.id !== 'emperor' || n.state !== 'ok'));
    const limitProblems = deckProblems(deck, CARD_DB).filter((p) => !p.startsWith('Your deck needs'));
    const needRow = el(
      'div',
      { class: 'needs' },
      el('span', { class: 'helper-label' }, archName ? `Needs · aiming for ${archName}` : 'Needs'),
      needs.map((n) =>
        el('span', { class: `need is-${n.state}`, title: n.hint }, n.id === 'emperor' ? (n.state === 'missing' ? 'No Emperor yet' : 'Too many Emperors') : `${n.label} ${n.have}/${n.aim}`),
      ),
      limitProblems.map((p) => el('span', { class: 'need is-over', title: p }, p)),
    );
    let suggestRow: HTMLElement;
    if (packLocked()) {
      const run = session.packRun!;
      suggestRow = el(
        'p',
        { class: 'helper-note' },
        run.opened.length < PACKS_PER_DECK
          ? `Made from packs: ${run.opened.length} of ${PACKS_PER_DECK} opened. Open Packs to keep going; Clear Deck to start over.`
          : 'Made from packs: all six are open. Save Deck to play with it, or Clear Deck to start over.',
      );
    } else if (emperors + main === 0) {
      suggestRow = el('p', { class: 'helper-note' }, 'Add a card or an Emperor and cards that fit your deck show up here.');
    } else {
      const list = suggestCards(deck, CARD_DB, 12, session.workingDeckAim);
      suggestRow = list.length
        ? el(
            'div',
            { class: 'suggest' },
            el('span', { class: 'helper-label' }, emperors === 0 ? (list.some((sg) => !isEmperor(sg.card)) ? 'The rest of your win condition, then Emperors that fit' : 'Emperors that fit') : 'Cards that fit'),
            el(
              'div',
              { class: 'suggest-row' },
              list.map((sg) =>
                tile(
                  sg.card,
                  () => tryAdd(sg.card),
                  () => enlarge(sg.card, false, list.map((x) => x.card)),
                  undefined,
                  el('span', { class: 'suggest-why' }, sg.why.slice(0, 2).join(' · ') || (sg.card.affiliations.includes('neutral') ? 'Neutral: fits anywhere' : '')),
                ),
              ),
            ),
          )
        : el('p', { class: 'helper-note' }, 'Your deck is full. Save Deck to play with it.');
    }
    helper.replaceChildren(needRow, suggestRow);
  }

  function tryAdd(card: CardDef) {
    if (packLocked()) return lockedToast();
    const why = whyCantAdd(deck, card, CARD_DB);
    if (why) {
      toast(why, 'error');
      return;
    }
    addCard(deck, card.number);
    refreshCounts();
  }

  /** " · leans Aggro, Tempo": the deck archetypes this card pulls a deck toward, for the Deck Stats guess. */
  function leanLine(card: CardDef) {
    const ids = [...leaningsOf(card).keys()];
    return ids.length ? ` · leans ${ids.map((id) => ARCHETYPES.find((a) => a.id === id)?.name ?? titleCase(id)).join(', ')}` : '';
  }

  /**
   * A card up close. `list` is the row of cards it was opened from (the grid as filtered and sorted, the deck,
   * or the suggestions): the left and right arrow keys step to the card before or after it there.
   */
  /** The − / + beside a card up close: how many copies are in the deck, one more or one fewer a click. */
  function copyStepper(card: CardDef) {
    const shown = el('span', { class: 'copy-count' });
    const minus = button('−', () => change(-1), 'btn-small copy-step', { title: 'Take one copy out (↓)', 'aria-label': 'Take one copy out' });
    const plus = button('+', () => change(1), 'btn-small copy-step', { title: 'Put one more copy in (↑)', 'aria-label': 'Put one more copy in' });
    const update = () => {
      const n = deck.get(card.number) ?? 0;
      shown.textContent = `${n} / ${card.limit} in your deck`;
      minus.disabled = n === 0;
      plus.disabled = n >= card.limit;
    };
    const change = (by: 1 | -1) => {
      if (by > 0) tryAdd(card);
      else removeOne(card);
      if (refreshDeckView && document.querySelector('.deck-view')) refreshDeckView();
      update();
    };
    closeUpRefresh = update;
    update();
    return el('div', { class: 'copy-stepper' }, minus, shown, plus);
  }

  function enlarge(card: CardDef, inDeckView = false, list: CardDef[] = gridOrder) {
    closeUpCard = card;
    const close = showCardLarge(
      cardFrontSrc(card.number),
      `${cardNo(card)} ${card.name}`,
      `${rarityLabel(card) ? `${rarityLabel(card)} · ` : ''}${titleCase(card.types.join('/'))} · cost ${costLabel(card)}${card.costDark ? ' Dark Gerzite' : ''} · ${card.affiliations.includes('unassigned') ? 'No affiliation' : titleCase(card.affiliations.join('/'))} · limit ${card.limit}${card.startingHand ? '' : ' · never in your starting hand'}${card.tiers > 1 ? ` · merges up to Tier ${roman(card.tiers)}` : ''}${card.arrival === 'ambush' ? ' · Ambush' : card.entrenchedTurns > 1 ? ` · arrives Entrenched for ${card.entrenchedTurns} turns` : ''}${card.discardAura ? ' · discard aura' : ''}${card.movement ? ` · ${titleCase(card.movement)}` : ''}${leanLine(card)}`,
      [],
      {
        rules: cardRulesNode(card),
        controls: copyStepper(card),
        // The Remaining: its hidden rules, in short
        ...(card.whileInPlay.includes('jumps')
          ? { alerts: ['Hidden mechanic: you can never draw this card or take it from a pile, and it is never in your starting hand. As each of your turns begins it jumps to a new place in whichever pile it is in. It comes to your hand only when a Calling Pillar stands in every one of your lanes.'] }
          : {}),
      },
    );
    const panel = [...document.querySelectorAll('.modal-card')].at(-1);
    const i = list.findIndex((c) => c.number === card.number);
    if (!panel || i < 0 || list.length < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (!panel.isConnected) return window.removeEventListener('keydown', onKey); // closed some other way
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if ([...document.querySelectorAll('.modal')].at(-1) !== panel) return; // something else is open on top of it
      e.preventDefault();
      window.removeEventListener('keydown', onKey);
      close();
      const next = list[(i + (e.key === 'ArrowRight' ? 1 : -1) + list.length) % list.length];
      enlarge(next, inDeckView, list);
    };
    window.addEventListener('keydown', onKey);
  }

  // ---------- grid ----------
  /**
   * Under a card, while Card Stats is on: "Use 12% · Win 54%" (from the games it was actually played in), and
   * below it "In decks 30% · Deck wins 51%" (from every deck it was in, played or not).
   */
  function statLine(card: CardDef) {
    // with Card Stats off, the same numbers still show over a card while the mouse is on it
    if (!liveStats) return '';
    const st = statsFor(liveStats, card.number);
    // (a share above 0 that would round to 0% shows as <1%, so a card that is rarely played doesn't look unplayed)
    const pctText = (x: number) => (x > 0 && Math.round(x * 100) === 0 ? '<1%' : `${Math.round(x * 100)}%`);
    const tone = (x: number | null) => (x === null ? 'muted' : x >= 0.5 ? 'is-good' : 'is-bad');
    const title = [
      `Use: played in ${st.played} of ${st.playDecks} decks in finished online games`,
      `Win: ${st.playedDecided ? `won ${st.playedWon} of ${st.playedDecided} games it was played in that had a winner` : 'not played in a game with a winner yet'}`,
      `Used in decks: in ${st.decks} of ${liveStats.decks} decks played online`,
      `Used in winning decks: ${st.decided ? `its decks won ${st.won} of ${st.decided} games with a winner` : 'no finished games yet'}`,
    ].join('\n');
    return el(
      'span',
      { class: `tile-stats${statsView.on ? '' : ' tile-stats-hover'}`, title },
      el('span', {}, `Use ${pctText(st.use)}`),
      el('span', { class: tone(st.win) }, `Win ${st.win === null ? '—' : pctText(st.win)}`),
      el('span', { class: 'tile-stats-sub' }, `${statsView.on ? 'In decks' : 'Decks'} ${pctText(st.inDecks)}`),
      el('span', { class: `tile-stats-sub ${tone(st.deckWin)}` }, `${statsView.on ? 'Deck wins' : 'D. wins'} ${st.deckWin === null ? '—' : pctText(st.deckWin)}`),
    );
  }

  function tile(card: CardDef, onClick: () => void, onRight: () => void, badge?: HTMLElement, extra?: HTMLElement) {
    const t = el(
      'button',
      { class: 'card-tile', type: 'button', 'data-num': String(card.number), title: `${card.name} — click to add, right-click to enlarge · ↑ / ↓ add or remove one` },
      setImgLazy(el('img', { class: 'card-thumb', alt: card.name, width: '500', height: '700' }), () => cardFrontSrc(card.number)),
      // (with Card Stats off, the numbers sit inside the name line and show in its place while the mouse is on
      // the card, so they never cover a card's picture, its own or the one below it)
      el('span', { class: 'tile-name' }, el('span', { class: 'tile-num' }, cardNo(card)), ` ${card.name}`, statsView.on ? '' : statLine(card)),
      statsView.on ? statLine(card) : '',
      extra ?? '',
      badge ?? '',
    );
    t.addEventListener('click', onClick);
    t.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      onRight();
    });
    return t;
  }

  let closeUpRefresh: (() => void) | null = null; // redraws the − / + count of the card up close
  let closeUpCard: CardDef | null = null; // the card last opened up close (↑ / ↓ work on it while it is open)
  let gridOrder: CardDef[] = []; // the cards in the grid, in the order shown (for stepping through close-ups)
  function renderGrid() {
    badges.clear();
    // (small screens) the Filters button says how many filters are on
    const active = filters.set.size + filters.rarity.size + filters.type.size + filters.cost.size + filters.affiliation.size + filters.leans.size + filters.roles.size + (filters.search ? 1 : 0);
    filtersToggle.textContent = active ? `Filters (${active})` : 'Filters';
    const shown = CARD_DB.list.filter(matches);
    gridOrder = shown;
    // the order picked in Sort (Most used and Best win % only while Card Stats is on)
    const by = view.sort;
    const stat = by.replace(/Low$/, '');
    if ((stat === 'use' || stat === 'win' || stat === 'inDecks' || stat === 'deckWin') && statsView.on && liveStats) {
      const st = liveStats;
      const low = by.endsWith('Low'); // lowest first
      const key = (c: CardDef) => statsFor(st, c.number)[stat];
      // cards with no games to tell from yet go last, whichever way the order runs
      shown.sort((a, b) => {
        const x = key(a);
        const y = key(b);
        if (x === null || y === null) return (x === null ? 1 : 0) - (y === null ? 1 : 0) || a.number - b.number;
        return (low ? x - y : y - x) || a.number - b.number;
      });
    } else if (by === 'name') shown.sort((a, b) => a.name.localeCompare(b.name));
    else if (by === 'cost') shown.sort((a, b) => (a.cost ?? 99) - (b.cost ?? 99) || a.number - b.number);
    else if (by === 'rarity') {
      const r = (c: CardDef) => (isEmperor(c) ? 99 : (RARITIES as readonly string[]).indexOf(c.rarity));
      shown.sort((a, b) => r(b) - r(a) || a.number - b.number);
    }
    grid.replaceChildren(
      ...shown.map((card) => {
        const badge = el('span', { class: 'tile-badge' });
        badges.set(card.number, badge);
        return tile(card, () => tryAdd(card), () => enlarge(card), badge);
      }),
    );
    if (shown.length === 0) grid.append(el('p', { class: 'empty-note' }, 'No cards match these filters. Try Reset Filters.'));
    resultCount.textContent = `${shown.length} of ${CARD_DB.list.length} cards`;
    refreshCounts();
  }

  // ---------- filters ----------
  const chipGroups: { set: Set<string | number>; chips: HTMLButtonElement[] }[] = [];
  function chipGroup<T extends string | number>(label: string, values: readonly T[], set: Set<T>, fmt: (v: T) => string) {
    const chips = values.map((v) => {
      const chip = el('button', { class: 'chip', type: 'button', 'aria-pressed': String(set.has(v)) }, fmt(v));
      chip.addEventListener('click', () => {
        if (set.has(v)) set.delete(v);
        else set.add(v);
        chip.setAttribute('aria-pressed', String(set.has(v)));
        renderGrid();
      });
      return chip;
    });
    chipGroups.push({ set: set as Set<string | number>, chips });
    return el('fieldset', { class: 'filter-group' }, el('legend', {}, label), el('div', { class: 'chips' }, chips));
  }

  const search = el('input', {
    id: 'card-search',
    class: 'text-input',
    type: 'search',
    placeholder: 'Search names, card text, an archetype or #number',
    value: filters.search,
  });
  search.addEventListener('input', () => {
    filters.search = search.value.trim();
    renderGrid();
  });

  const resetFilters = () => {
    filters.set.clear();
    filters.rarity.clear();
    filters.type.clear();
    filters.cost.clear();
    filters.affiliation.clear();
    filters.leans.clear();
    filters.roles.clear();
    filters.search = '';
    search.value = '';
    for (const g of chipGroups) for (const c of g.chips) c.setAttribute('aria-pressed', 'false');
    renderGrid();
  };

  const filterPanel = el(
    'aside',
    { class: 'filters' },
    search,
    chipGroup('Set', Object.keys(CONFIG.sets), filters.set, (id) => CONFIG.sets[id]?.name ?? id),
    chipGroup('Rarity', RARITIES, filters.rarity, titleCase),
    chipGroup('Card type', CARD_TYPES, filters.type, titleCase),
    chipGroup('Gerzite cost', COSTS, filters.cost, String),
    chipGroup('Affiliation', AFFILIATIONS, filters.affiliation, titleCase),
    chipGroup('Role', ROLES, filters.roles, titleCase),
    chipGroup('Leans toward', ARCHETYPE_IDS, filters.leans, (id) => ARCHETYPES.find((a) => a.id === id)?.name ?? titleCase(id)),
    button('Reset Filters', resetFilters, 'btn-ghost btn-wide'),
  );

  // ---------- deck actions ----------
  function saveDeck() {
    const problems = deckProblems(deck, CARD_DB);
    if (problems.length) {
      toast(`Can’t save yet: ${problems[0]}`, 'error');
      return false;
    }
    session.savedDeck = new Map(deck);
    session.savedDeckName = deckName();
    rememberSavedDeck(deck, deckName());
    refreshCounts();
    toast(`“${deckName()}” saved. It’s the deck you’ll play with, and it stays saved in this browser.`, 'ok');
    afterSave?.();
    return true;
  }

  function showExport(after?: () => void) {
    const text = exportDeck(deck, CARD_DB, deckName());
    const area = el('textarea', { class: 'deck-text', id: 'export-text', readonly: true, rows: '14' });
    area.value = text;
    let close = () => {};
    close = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, 'Export deck'),
        el('p', { class: 'muted' }, 'Copy this text and keep it anywhere (a Google Doc, a message to a friend). Paste it into Import Deck to load it again.'),
        area,
        el('div', { class: 'row-end' }, button('Copy', () => copyText(text, area), 'btn-primary'), button('Close', () => close(), 'btn-ghost')),
      ),
      { onClose: () => after?.() },
    );
    area.select();
  }

  function showImport(after: () => void) {
    const area = el('textarea', { class: 'deck-text', id: 'import-text', rows: '14', placeholder: '1x #2 Card 2\n12x #9 Card 9\n…' });
    let close = () => {};
    const load = () => {
      const { deck: loaded, name, warnings } = importDeck(area.value, CARD_DB);
      for (const w of warnings) toast(w, 'error');
      if (loaded.size === 0) return;
      deck.clear();
      session.workingDeckAim = null;
      for (const [k, v] of loaded) deck.set(k, v);
      if (name !== null) {
        session.workingDeckName = name;
        nameInput.value = name;
      }
      close();
      refreshCounts();
      after();
      const problems = deckProblems(deck, CARD_DB);
      toast(problems.length ? `Deck loaded. Before saving: ${problems[0]}` : 'Deck loaded. Press Save Deck to play with it.', problems.length ? 'info' : 'ok');
    };
    close = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, 'Import deck'),
        el('p', { class: 'muted' }, 'Paste an exported deck. This replaces the cards currently in the builder.'),
        area,
        el('div', { class: 'row-end' }, button('Load Deck', load, 'btn-primary'), button('Cancel', () => close(), 'btn-ghost')),
      ),
    );
    area.focus();
  }

  let refreshDeckView: (() => void) | null = null; // View Deck, while it is open: draws it again after a change
  function showDeckView() {
    const body = el('div', { class: 'deck-view-body' });
    const counts = el('p', { class: 'muted' });
    const render = () => {
      const entries = deckEntries(deck, CARD_DB);
      const { emperors, main } = deckStats(deck, CARD_DB);
      counts.textContent = `Emperor ${emperors}/1 · Cards ${main}/${MAIN_DECK_SIZE} · ${isTouchScreen() ? 'tap a card to remove one copy, press and hold to enlarge' : 'click a card to remove one copy, right-click to enlarge'}`;
      const section = (title: string, list: [CardDef, number][]) =>
        el(
          'section',
          { class: 'deck-section' },
          el('h3', {}, title),
          list.length
            ? el(
                'div',
                { class: 'card-grid card-grid-small' },
                list.map(([card, n]) =>
                  tile(
                    card,
                    () => {
                      if (packLocked()) return lockedToast();
                      removeCard(deck, card.number);
                      refreshCounts();
                      render();
                    },
                    () => enlarge(card, true, list.map(([c]) => c)),
                    el('span', { class: 'tile-badge' }, `×${n}`),
                  ),
                ),
              )
            : el('p', { class: 'empty-note' }, 'None yet.'),
        );
      body.replaceChildren(
        section('Emperor', entries.filter(([c]) => isEmperor(c))),
        section(`Main deck (${main})`, entries.filter(([c]) => !isEmperor(c))),
      );
    };
    render();
    refreshDeckView = render;
    let close = () => {};
    close = modal(
      el(
        'div',
        { class: 'stack deck-view' },
        el('div', { class: 'deck-view-head' }, el('h2', {}, deckName()), counts),
        body,
        el(
          'div',
          { class: 'row-end deck-view-foot' },
          button('Deck Stats', () => showDeckStats(deck, deckName())),
          button('Save Deck', () => saveDeck() && close(), 'btn-primary'),
          button('Close', () => close(), 'btn-ghost'),
        ),
      ),
      { wide: true },
    );
  }

  /** Empties the builder, after asking: a deck takes a while to put together. */
  const clearDeck = () => {
    const { emperors, main } = deckStats(deck, CARD_DB);
    if (emperors + main === 0) {
      if (session.packRun) {
        session.packRun = null;
        refreshCounts();
        toast('Back to building by hand.', 'info');
      } else toast('The deck is already empty.', 'info');
      return;
    }
    const what = [emperors ? 'the Emperor' : '', main ? `${main} card${main === 1 ? '' : 's'}` : ''].filter(Boolean).join(' and ');
    let close = () => {};
    close = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, 'Clear this deck?'),
        el('p', { class: 'muted' }, `This takes ${what} out of the builder. Your saved deck stays as it is until you save again.`),
        el(
          'div',
          { class: 'row-end' },
          button('Clear Deck', () => {
            close();
            deck.clear();
            session.packRun = null;
            session.workingDeckAim = null;
            refreshCounts();
            toast('Deck cleared. Your saved deck is unchanged until you save again.', 'info');
          }, 'btn-danger'),
          button('Keep It', () => close(), 'btn-ghost'),
        ),
      ),
    );
  };
  /** Random Deck keeps what is already in the builder and fills the open slots around it. */
  const fillRandom = () => {
    if (packLocked()) return lockedToast();
    const before = deckStats(deck, CARD_DB);
    if (before.main >= MAIN_DECK_SIZE && before.emperors >= 1) {
      toast('Your deck is already full. Take some cards out and Random Deck fills the gaps, or Clear Deck for a whole new one.', 'info');
      return;
    }
    const r = buildRandomDeck(CARD_DB, Math.random, deck);
    if (!r) {
      toast('There aren’t enough cards in cards.csv to fill a deck.', 'error');
      return;
    }
    deck.clear();
    for (const [k, v] of r.deck) deck.set(k, v);
    session.workingDeckAim = r.theme.archetypes;
    refreshCounts();
    const theme = themeText(r.theme, (id) => ARCHETYPES.find((a) => a.id === id)?.name ?? titleCase(id));
    const emperor = r.emperorPicked ? ' and picked an Emperor' : '';
    toast(
      r.kept > 0 || !r.emperorPicked
        ? `Kept what you had, filled ${r.added} open slot${r.added === 1 ? '' : 's'}${emperor}: ${theme}. Save Deck to play with it.`
        : `Random deck laid out: ${theme}. Save Deck to play with it.`,
      'ok',
    );
  };

  // ---------- Open Packs ----------
  const packPicker = () =>
    showPackPicker({
      run: session.packRun!,
      deck,
      onLand: (card) => {
        addCard(deck, card.number);
        refreshCounts();
        const counter = isEmperor(card) ? emperorCount : mainCount;
        counter.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }], { duration: 220 });
      },
      target: (card) => (isEmperor(card) ? emperorCount : mainCount),
      onComplete: () => {
        refreshCounts();
        toast('All six packs are open: that’s a full deck. Save Deck to play with it.', 'ok');
      },
    });
  const openPacks = () => {
    const run = session.packRun;
    if (run) {
      if (run.opened.length >= PACKS_PER_DECK) {
        toast('Every pack for this deck is open. Clear Deck to start a new one.', 'info');
        return;
      }
      packPicker();
      return;
    }
    const { emperors, main } = deckStats(deck, CARD_DB);
    const start = () => {
      deck.clear();
      session.packRun = { opened: [] };
      session.workingDeckAim = null;
      refreshCounts();
      packPicker();
    };
    if (emperors + main === 0) return start();
    let close = () => {};
    close = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, 'Build this deck from packs?'),
        el('p', { class: 'muted' }, `A pack deck starts empty: this clears the ${emperors + main} card${emperors + main === 1 ? '' : 's'} in the builder. Your saved deck stays as it is until you save again. Once the packs are open, the deck can’t be changed by hand (Clear Deck starts over).`),
        el(
          'div',
          { class: 'row-end' },
          button('Clear & Open Packs', () => {
            close();
            start();
          }, 'btn-primary'),
          button('Cancel', () => close(), 'btn-ghost'),
        ),
      ),
    );
  };

  // ---------- Card Stats ----------
  const statsToggle = button('Card Stats', () => void setStats(!statsView.on), 'btn-small', { 'aria-pressed': String(statsView.on), title: 'Show how often each card is played online, and how often its decks win' });
  const sortOptions = () => {
    const opts: [SortBy, string][] = [['set', 'Set number'], ['name', 'Name'], ['cost', 'Cost'], ['rarity', 'Rarity']];
    if (statsView.on)
      opts.push(
        ['use', 'Use %: highest first'], ['useLow', 'Use %: lowest first'],
        ['inDecks', 'Deck use %: highest first'], ['inDecksLow', 'Deck use %: lowest first'],
        ['win', 'Win %: highest first'], ['winLow', 'Win %: lowest first'],
        ['deckWin', 'Deck win %: highest first'], ['deckWinLow', 'Deck win %: lowest first'],
      );
    if (!opts.some(([v]) => v === view.sort)) view.sort = 'set';
    sortSelect.replaceChildren(...opts.map(([v, label]) => el('option', { value: v, selected: view.sort === v }, label)));
  };
  const sortSelect = el('select', { class: 'stats-order', id: 'sort-cards', 'aria-label': 'Sort the cards by' }) as HTMLSelectElement;
  sortSelect.addEventListener('change', () => {
    view.sort = sortSelect.value as SortBy;
    renderGrid();
  });
  sortOptions();
  const sortField = el('label', { class: 'sort-field' }, el('span', { class: 'field-label' }, 'Sort'), sortSelect);
  const helperToggle = button('Deck Helper', () => {
    view.helper = !view.helper;
    helperToggle.setAttribute('aria-pressed', String(view.helper));
    renderHelper();
  }, 'btn-small', { 'aria-pressed': String(view.helper), title: 'What your deck still needs, and cards that fit it' });
  async function setStats(on: boolean) {
    if (on) {
      const stats = await loadCardStats();
      if (!stats) {
        toast('Card stats come from online games, so they show when the game is opened from its online address.', 'info');
        return;
      }
      liveStats = stats;
      if (!stats.decks) toast('No online games have been counted yet: play some and check back.', 'info');
    }
    statsView.on = on;
    statsToggle.setAttribute('aria-pressed', String(on));
    sortOptions();
    renderGrid();
  }

  // on a small screen the filters fold away above the cards: this opens them
  const filtersToggle: HTMLButtonElement = button('Filters', () => {
    const open = screen.classList.toggle('filters-open');
    filtersToggle.setAttribute('aria-expanded', String(open));
    if (open) filterPanel.scrollIntoView({ block: 'start' });
  }, 'btn-small filters-toggle', { 'aria-expanded': 'false' });
  const screen = el(
    'div',
    { class: 'builder' },
    el(
      'header',
      { class: 'builder-head' },
      button('← Menu', onBack, 'btn-ghost'),
      el('h1', { class: 'builder-title' }, 'Deck Builder'),
      el('label', { class: 'deck-name-field' }, el('span', { class: 'field-label' }, 'Deck name'), nameInput),
      helperToggle,
      statsToggle,
      sortField,
      resultCount,
      filtersToggle,
    ),
    el('div', { class: 'builder-body' }, filterPanel, el('section', { class: 'builder-cards' }, helper, grid)),
    el(
      'footer',
      { class: 'builder-foot' },
      el(
        'div',
        { class: 'foot-buttons foot-left' },
        button('Open Packs', openPacks, 'btn-packs', { id: 'open-packs', title: 'Make a random deck the fun way: rip open the Emperor Pack and five more' }),
        button('Random Deck', fillRandom, 'btn-ghost', { title: 'Fills the open slots at random around a theme: one or two affiliations and archetypes. Cards already in the deck are kept' }),
        button('Clear Deck', clearDeck, 'btn-ghost', { title: 'Takes every card out of the builder (asks first)' }),
      ),
      el(
        'div',
        { class: 'deck-counts' },
        el('span', { class: 'count' }, el('span', { class: 'count-label' }, 'Emperor'), emperorCount),
        el('span', { class: 'count' }, el('span', { class: 'count-label' }, 'Cards'), mainCount),
        packState,
        saveState,
      ),
      el(
        'div',
        { class: 'foot-buttons' },
        button('Deck Stats', () => showDeckStats(deck, deckName()), '', { title: 'Cost curve, card types, rarity, and a guess at the deck’s archetype' }),
        button('Import Deck', () => (packLocked() ? lockedToast() : showImport(() => {})), '', { title: 'Load a deck you exported before (replaces the cards in the builder)' }),
        button('Export Deck', () => showExport(), '', { title: 'Copy this deck as text: to keep several decks, or to move one to another computer or browser' }),
        button('View Deck', showDeckView),
        button('Save Deck', saveDeck, 'btn-primary'),
      ),
    ),
  );

  // ---------- ↑ / ↓ over a card: add or take out one copy ----------
  // Works on the card under the mouse (in the grid, the suggestions or View Deck) and on a card open up close.
  // the card the mouse was last moved onto: it stays the one ↑ / ↓ work on even if the cards shift under the
  // mouse (the Deck Helper growing as the first card goes in, View Deck redrawing)
  let pointed: { num: number; inModal: Element | null } | null = null;
  const onMove = (e: MouseEvent) => {
    const tileEl = (e.target as HTMLElement | null)?.closest?.<HTMLElement>('.card-tile[data-num]');
    pointed = tileEl ? { num: Number(tileEl.dataset.num), inModal: tileEl.closest('.modal') } : null;
  };
  function removeOne(card: CardDef) {
    if (packLocked()) return lockedToast();
    if (!deck.get(card.number)) return toast(`${card.name} isn’t in your deck.`, 'info');
    removeCard(deck, card.number);
    refreshCounts();
  }
  const onArrow = (e: KeyboardEvent) => {
    if (!screen.isConnected) {
      window.removeEventListener('keydown', onArrow);
      window.removeEventListener('mousemove', onMove);
      return;
    }
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    if ((e.target as HTMLElement | null)?.closest?.('input, textarea, select')) return;
    const top = [...document.querySelectorAll('.modal')].at(-1);
    let card: CardDef | undefined;
    if (top?.classList.contains('modal-card')) card = closeUpCard ?? undefined; // a card open up close
    else if (pointed && (pointed.inModal ?? null) === (top ?? null)) card = CARD_DB.byNumber.get(pointed.num); // a tile behind a pop-up doesn't count
    const tileEl = card ? ([...document.querySelectorAll<HTMLElement>(`.card-tile[data-num="${card.number}"]`)].find((t) => !top || top.contains(t)) ?? null) : null;
    if (!card) return;
    e.preventDefault();
    if (e.key === 'ArrowUp') tryAdd(card);
    else removeOne(card);
    if (refreshDeckView && document.querySelector('.deck-view')) refreshDeckView();
    if (top?.classList.contains('modal-card')) closeUpRefresh?.();
    tileEl?.animate([{ transform: 'scale(1)' }, { transform: `scale(${e.key === 'ArrowUp' ? 1.06 : 0.94})` }, { transform: 'scale(1)' }], { duration: 160 });
  };
  window.addEventListener('keydown', onArrow);
  window.addEventListener('mousemove', onMove);

  document.getElementById('app')!.replaceChildren(screen);
  renderGrid();
  if (statsView.on) void setStats(true); // left on last time: fetch the latest numbers
  // off: fetch them quietly anyway, so a card shows its numbers while the mouse is over it
  else void loadCardStats().then((stats) => {
    if (!stats?.decks || liveStats || !screen.isConnected) return;
    liveStats = stats;
    renderGrid();
  });
}
